import {
  BluetoothAudio,
  Build,
  BuildCircleOutlined,
  CheckBoxOutlineBlank,
  ControlPoint,
  DashboardOutlined,
  DevicesOther,
  GpsFixed,
  Router,
  RemoveCircleOutline,
  Shield,
  ShieldOutlined,
  ShowChart,
} from '@mui/icons-material';
import { Grid, Stack, Typography } from '@mui/material';
import { transform } from 'ol/proj';
import { Geometry, LineString, MultiPolygon, Point, Polygon } from 'ol/geom';
import {
  AssetEntity,
  EntityType,
  FenceEntityType,
  FenceZone,
  GeofenceEntityType,
  MicrofenceEntity,
} from '../../util/enums';
import { jsUcFirst } from '../Global/StringFormatterFunctions';
import { GeofenceFilter, NameId } from './types';
import { Feature, Map as OlMap, View } from 'ol';
import VectorLayer from 'ol/layer/Vector';
import AnimatedCluster from 'ol-ext/layer/AnimatedCluster';
import {
  ACTIVE_GREEN,
  CLEARED_ZONE,
  lineLimitStyle,
  PRIMARY,
  SECONDARY,
  truncationStyle,
} from '../../Style/GeoMobyBaseTheme';
import { AssetState } from '../../util/Events/Messages';
import {
  FRESH,
  EVENT_DATA,
  GEOFENCE_EVENT_LAYER,
  HISTORICAL_GEOFENCE_EVENT_LAYER,
  IS_INVISIBLE,
  MICROFENCE_LAYER_ID,
  PIN_LAYER,
  TRACKING_BOUNDS,
  UPDATED,
  ASSET_LAYER,
  ASSET_DATA,
  HEATMAP_CONENTRATION,
} from '../../util/constants';
import { DurationDescription } from '../../Common/Sidebar';
import { stringifyIdRecord } from '../../util/stringUtils';
import { assetTypeOfEntity, geometryTypeOfEntity } from './commons';
import { defaultDropPinStyle, geofenceStyleDefaults } from './Styles/MiscStyles';
import VectorSource from 'ol/source/Vector';
import { fitToExtent } from '../../hooks/geomoby/MapAnimation';
import { GridRowData } from '@material-ui/data-grid';
import { searchPropertyName, searchPropertyValue } from '../GeomobyProperties/GeomobyProperties';
import { compareDesc } from 'date-fns';
import { AssetCoordinatesDwell, AssetCoordinatesTriggered } from '../../util/Events/schema';
import { updateGeofencesStyle } from './Styles/GeofenceStyles';
import RenderFeature from 'ol/render/Feature';
import { isMicrofenceType } from '../Microfence/helpers';
import styles from './../../Common/truncate.module.css';

export const AssetPrefixForAsset = (assetEntity: AssetEntity) => {
  switch (assetEntity) {
    case AssetEntity.Beacon:
      return <BluetoothAudio />;
    case AssetEntity.Device:
      return <DevicesOther />;
    case AssetEntity.GpsTracker:
      return <GpsFixed />;
    case AssetEntity.MqttGateway:
      return <Router />;
    case AssetEntity.Tool:
      return <Build />;
  }
};

export const AssetPrefixForActiveAsset = (asset: AssetState) => (
  <>
    {asset.microfence ? (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {MicrofenceIcon(asset.microfence.type)}
        <Typography>[{EntityType.Microfence}]</Typography>
      </Stack>
    ) : (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {AssetPrefixForAsset(asset.type ?? AssetEntity.Unknown)}
        <Typography>[{(asset.type ?? AssetEntity.Unknown).toUpperCase()}]</Typography>
      </Stack>
    )}
  </>
);

export const getAssetType = (ids: Record<string, string>): string => {
  const type = assetTypeOfEntity(ids);
  if (type === AssetEntity.GpsTracker) {
    return 'GPS Tracker';
  }
  return jsUcFirst(type);
};

export const getAssetTypeTitle = (assetEntity: AssetEntity | EntityType) => {
  if (assetEntity === AssetEntity.GpsTracker || assetEntity === EntityType.GpsTracker) {
    return 'GPS Tracker';
  }
  if (assetEntity === AssetEntity.MqttGateway) {
    return 'MQTT Gateway';
  }
  return jsUcFirst(assetEntity);
};

export const DisplayAssetHeading = (label: string | undefined, id: string, type: AssetEntity) => {
  return (
    <Grid
      container
      direction="row"
      spacing={1}
      marginLeft={0}
      justifyContent="left"
      alignItems="center"
    >
      <Typography variant="h5" className={styles.truncateUnlessHover}>
        {(label || id) && <span style={lineLimitStyle}>{label || id}</span>}
        {!(label || id) && (
          <span
            style={{
              color: SECONDARY,
            }}
          >
            {`${type} IS UNKNOWN`}
          </span>
        )}
      </Typography>
    </Grid>
  );
};

export const AssetLabel = (label: string | undefined, id: string) => {
  const identifier = id || 'UNKNOWN ID';
  const hasLabel = Boolean(label);

  return (
    <>
      {hasLabel ? (
        label
      ) : (
        <>
          <span style={{ color: SECONDARY }}>NO LABEL</span>
          {` (${identifier})`}
        </>
      )}
    </>
  );
};

type EndsWithColon<T extends string> = `${T}:`;
const idLabelByType: Partial<Record<AssetEntity, EndsWithColon<string>>> = {
  [AssetEntity.GpsTracker]: 'Serial:',
  [AssetEntity.MqttGateway]: 'MQTT Gateway ID:',
};
export const AssetIdOrUnknown = (id: string | undefined, type: AssetEntity) => {
  const idLabel = idLabelByType[type] ?? `${jsUcFirst(type)} ID:`;
  return (
    <Grid container direction={'row'}>
      <Typography
        className={styles.truncateUnlessHover}
        style={{
          color: PRIMARY,
          paddingRight: '5px',
          width: 'fit-content',
        }}
      >
        {idLabel}
      </Typography>

      <Typography
        className={styles.truncateUnlessHover}
        style={{
          width: 'fit-content',
        }}
      >
        {id ? id : 'UNKNOWN ID'}
      </Typography>
    </Grid>
  );
};

export const ActiveAssetText = () => (
  <Typography
    style={{
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    <span
      style={{
        marginLeft: '10px',
        color: ACTIVE_GREEN,
        ...truncationStyle,
      }}
    >
      ACTIVE
    </span>
  </Typography>
);

export const AssetActivityStatus = ({
  isActive,
  sinceIso8601,
  nowIso8601,
}: {
  isActive: boolean;
  sinceIso8601?: string;
  nowIso8601: string;
}) => {
  return (
    <>
      <Grid item container direction={'row'} xs={12} fontSize="90%">
        <Typography style={{ color: PRIMARY, paddingRight: '5px' }}>Status:</Typography>
        <Typography style={{ color: isActive ? ACTIVE_GREEN : SECONDARY }}>
          {isActive ? 'ACTIVE' : 'NOT CURRENTLY ACTIVE'}
        </Typography>
        {isActive && sinceIso8601 && (
          <Typography
            component="div"
            display="inline-block"
            marginLeft="5px"
            alignSelf="center"
            flexGrow={2}
            textAlign="end"
          >
            <DurationDescription earlierIso8601={sinceIso8601} laterIso8601={nowIso8601} />
          </Typography>
        )}
      </Grid>
    </>
  );
};

export const applyGeofenceFilterToLayerOnMap = (
  layer: VectorLayer<VectorSource<Geometry>>,
  geofenceFilter: (GeofenceFilter & { geofenceIds?: string[] }) | undefined,
) => {
  const filters = Object.fromEntries(
    Object.entries(geofenceFilter ?? {}).filter(([key]) => key !== 'layerId'),
  );

  layer
    .getSource()
    .getFeatures()
    .forEach((f: Feature<Geometry>) => {
      let numberOfFilters = 0;
      const passedFilters = Object.entries(filters).map(([filterName, value]) => {
        if (value === undefined) return;
        if (filterName === 'orderBy' || filterName === 'order') return false;
        numberOfFilters += 1;
        if (filterName === 'geofenceIds') {
          return !!Array.from(value).find(v => v.includes(f.get('id').toLowerCase()));
        }
        if (filterName === 'searchName') {
          return !!f.get('name').toLowerCase().includes(value.toString()?.toLowerCase());
        }
        if (filterName === 'fenceType') {
          const type = geometryTypeOfEntity(f.getProperties()) as unknown as
            | GeofenceEntityType
            | undefined;
          return (
            (!value && f.get('zone') === FenceZone.cleared) ||
            !!(value as GeofenceEntityType[])?.find(t => t === type)
          );
        }
        if (filterName === 'zone') {
          const zone = f.get('zone') ?? FenceZone.none;
          return !!((value ?? FenceZone.none) === zone);
        }
        if (filterName === 'searchGeomobyKey') {
          return !!(value && !!searchPropertyName(f.get('geomobyProperties'), value as string));
        }
        if (filterName === 'searchGeomobyValue') {
          return !!(value && !!searchPropertyValue(f.get('geomobyProperties'), value as string));
        }
      });
      f.set(IS_INVISIBLE, passedFilters.filter(p => !!p).length !== numberOfFilters);
    });
};

export const distanceBetweenGpsCoords = (a: [number, number], b: [number, number]) =>
  new LineString([a, b].map(coord => transform(coord, 'EPSG:4326', 'EPSG:3857'))).getLength();

export const transformMultipolygon = (
  coordinates: number[][][][],
  from: 'EPSG:4326' | 'EPSG:3857',
): number[][][][] => {
  const to = from === 'EPSG:4326' ? 'EPSG:3857' : 'EPSG:4326';
  return coordinates.map(polygon =>
    polygon.map(ring => ring.map(([x, y]) => transform([x, y], from, to))),
  );
};

export const deselectFences = (olmap?: OlMap, exceptFenceId?: string) => {
  if (!olmap) return;

  const isSelected = (f: Feature<Geometry>) => f.get('id') === exceptFenceId && !!exceptFenceId;
  const updateFeaturesSelection = (features?: Feature<Geometry>[]) => {
    features?.filter(f => f.get('id')).forEach(f => f.set('selected', isSelected(f)));
  };

  olmap
    .getAllLayers()
    .filter(layer => layer instanceof VectorLayer)
    .forEach(layer => {
      const features = layer.getSource()?.getFeatures?.();
      updateFeaturesSelection(features);
    });

  const microfenceLayer = olmap
    .getAllLayers()
    .find(layer => layer instanceof VectorLayer && layer.get('id') === MICROFENCE_LAYER_ID);

  if (microfenceLayer) {
    const source = microfenceLayer.getSource?.();
    const features =
      source?.getFeatures?.().length > 0
        ? source?.getFeatures?.()
        : source?.source?.getFeatures?.();
    updateFeaturesSelection(features);
  }

  const animatedCluster = olmap
    .getAllLayers()
    .find(layer => layer instanceof AnimatedCluster && layer.get('id') === MICROFENCE_LAYER_ID);

  const clusteredFeatures = animatedCluster
    ?.getSource()
    ?.getFeatures()
    ?.flatMap((f: Feature<Geometry>) => f.get('features'));
  updateFeaturesSelection(clusteredFeatures);
};

export const deselectAssets = (olmap: OlMap | undefined) => {
  if (!olmap) return;
  olmap
    .getAllLayers()
    .filter(l => l instanceof VectorLayer && l.getClassName() === ASSET_LAYER)
    .forEach(l => {
      l.getSource()
        .getFeatures()
        .forEach((f: Feature<Geometry>) => {
          f.set(ASSET_DATA, {
            ...f.get(ASSET_DATA),
            selected: false,
          });
          f.set('selected', false);
        });
    });
};

export const deselectGeofenceEvents = (
  olmap: OlMap | undefined,
  exceptGeofenceEventId?: string,
) => {
  if (!olmap) return;
  olmap
    .getAllLayers()
    .filter(
      l =>
        l instanceof VectorLayer &&
        (l.getClassName() === GEOFENCE_EVENT_LAYER ||
          l.getClassName() === HISTORICAL_GEOFENCE_EVENT_LAYER),
    )
    .forEach(l => {
      l.getSource()
        .getFeatures()
        .forEach((f: Feature<Geometry>) => {
          f.set(EVENT_DATA, {
            ...f.get(EVENT_DATA),
            selected: exceptGeofenceEventId === f.get(EVENT_DATA)?.id,
          });
        });
    });
};

export const ZoneIcon = (zone: FenceZone) => {
  const defaultColour = `rgb(${geofenceStyleDefaults.default.stroke})`;
  return (
    <>
      {zone === FenceZone.breach && <Shield style={{ color: 'red' }} />}
      {zone === FenceZone.buffer && <ShieldOutlined style={{ color: 'orange' }} />}
      {zone === FenceZone.cleared && <DashboardOutlined style={{ color: CLEARED_ZONE }} />}
      {zone === FenceZone.dumping && <RemoveCircleOutline style={{ color: defaultColour }} />}
      {zone === FenceZone.loading && <ControlPoint style={{ color: defaultColour }} />}
      {zone === FenceZone.maintenance && <BuildCircleOutlined style={{ color: defaultColour }} />}
    </>
  );
};

export const FenceTypeIcon = (type: GeofenceEntityType) => {
  return (
    <>
      {type === GeofenceEntityType.Line && <ShowChart />}
      {type === GeofenceEntityType.Multipolygon && <DashboardOutlined />}
      {type === GeofenceEntityType.Polygon && <CheckBoxOutlineBlank />}
    </>
  );
};

export const MicrofenceIcon = (type: MicrofenceEntity) => {
  return (
    <>
      {type === MicrofenceEntity.Beacon && (
        <img
          style={{ margin: '-12px 8px -12px 0px' }}
          src="beaconFar.svg"
          alt="Beacon Microfence"
        />
      )}
      {type === MicrofenceEntity.Device && (
        <img
          style={{ margin: '-16px -4px -28px -2px' }}
          src="smartphoneFar.svg"
          alt="Device Microfence"
        />
      )}
      {type === MicrofenceEntity.Gateway && (
        <img
          style={{ margin: '-20px -66px -25px 0px' }}
          src="routerFar.svg"
          alt="Gateway Microfence"
        />
      )}
      {!isMicrofenceType({ type }) && (
        <>
          <span
            style={{
              padding: '5px',
            }}
          >
            [UNKNOWN TYPE]
          </span>
        </>
      )}
    </>
  );
};

export const entityTypeToId = (type: AssetEntity): string => {
  if (!Object.values(AssetEntity).includes(type)) return '';
  if (type === AssetEntity.GpsTracker) {
    return 'gpsTrackerId';
  }
  if (type === AssetEntity.Tool) {
    return 'beaconId';
  }
  return `${type}Id`;
};

export const typeAndIdToAssetStateKey = (type: AssetEntity, id: string): string => {
  return stringifyIdRecord({ [entityTypeToId(type)]: id });
};

export const getLayerFromMap = (
  olmap: OlMap,
  id: string | undefined,
  name?: string,
): VectorLayer<VectorSource<Geometry>> | undefined => {
  const layer = olmap
    ?.getAllLayers()
    .find(layer =>
      layer.get('id') === MICROFENCE_LAYER_ID
        ? layer.get('name') === name || layer.get('id') === id
        : layer instanceof VectorLayer &&
          ((!!name && (layer.getSource().get('name') ?? layer.get('name')) === name) ||
            (layer.getSource().get('id') ?? layer.get('id')) === id),
    );
  if (!layer) {
    name
      ? console.error('map layer not found for name', name)
      : console.error('map layer not found for id', id);
    return;
  }

  if (layer.getProperties().id === MICROFENCE_LAYER_ID)
    return layer as VectorLayer<VectorSource<Geometry>>;
  return layer as VectorLayer<VectorSource<Geometry>>;
};

export const findBufferZone = (olmap: OlMap, breachId: string, layerId: string) => {
  return getLayerFromMap(olmap, layerId)
    ?.getSource()
    ?.getFeatures()
    .filter(f => f.get('zone') === FenceZone.buffer)
    .find(f => f.get('parentId') === breachId);
};

export const setFreshLayerName = (layerIds: NameId[]): string => {
  const groupLayerCount = layerIds
    .filter(l => l.name.startsWith('Group '))
    .map(l => Number(l.name.substr(6, l.name.length)))
    .filter(n => !isNaN(n));
  // Size is length, as this does not include microfences.
  return `Group ${Math.max(layerIds.length - 1, ...groupLayerCount) + 1}`;
};

export const animateToLocation = async (
  view: View,
  [lon1, lat1, lon2, lat2]: [number, number, number, number],
  onFinish?: () => void,
): Promise<void> => {
  return new Promise<void>(resolve => {
    view.animate(
      {
        center: [lon1, lat1],
        zoom: isNaN(Number(view.getZoom())) ? 10 : Number(view.getZoom()),
        duration: 1000,
      },
      () => {
        fitToExtent(view, [lon1, lat1, lon2, lat2]);
        onFinish?.();
        resolve();
      },
    );
  });
};

export const dropLocationMarker = async (olmap: OlMap, pin: Feature<Point>) => {
  const pinLayer = olmap?.getAllLayers().find(layer => layer?.getClassName() === PIN_LAYER);
  if (pinLayer) {
    pinLayer.getSource()?.forEachFeature((feature: Feature<Geometry>) => {
      pinLayer.getSource().removeFeature(feature);
    });
    pinLayer.getSource().addFeatures([pin]);
  } else {
    olmap?.addLayer(
      new VectorLayer({
        className: PIN_LAYER,
        source: new VectorSource({
          features: [pin],
        }),
        style: defaultDropPinStyle,
      }),
    );
  }
};

export const entityHasChanged = (entity: Record<string, unknown>) => {
  let hasChanged = false;
  Object.entries(entity)
    .map(([key, value]) => ({ key, value }))
    .forEach(param => {
      if (param.key === 'uuid' && typeof param.value === 'string' && param.value.includes(FRESH)) {
        hasChanged = true;
      } else if (param.key === FRESH && param.value === true) {
        hasChanged = true;
      } else if (param.key === UPDATED && param.value === true) {
        hasChanged = true;
      }
    });
  return hasChanged;
};

export const createFeatureFromTypeAndCoordinates = (geofence: GridRowData) => {
  const fenceType = geometryTypeOfEntity(geofence);
  return fenceType === FenceEntityType.Line
    ? new LineString(geofence.points.coordinates)
    : fenceType === FenceEntityType.Multipolygon
    ? new MultiPolygon(geofence.points.coordinates)
    : new Polygon(geofence.points.coordinates);
};

export const getTrackingBoundEventsForAsset = (
  assetState: AssetState,
): AssetCoordinatesTriggered[] | undefined => {
  return assetState?.recentFenceEvents
    ?.filter(
      e =>
        e.layerIds.label === TRACKING_BOUNDS &&
        e.fenceIds.type !== GeofenceEntityType.Line &&
        'entered' in e,
    )
    .sort((a, b) => compareDesc(new Date(a.iso8601), new Date(b.iso8601)) || b.order - a.order) as
    | AssetCoordinatesTriggered[]
    | undefined;
};

export const setVisibleVectorLayerAndFeatures = ({
  olmap,
  geofenceFilter,
}: {
  olmap: OlMap;
  geofenceFilter?: GeofenceFilter & { geofenceIds?: string[] };
}) => {
  olmap
    .getAllLayers()
    .filter(lyr => lyr instanceof VectorLayer && lyr.getClassName() !== HEATMAP_CONENTRATION)
    .forEach(lyr => {
      const layer = lyr as VectorLayer<VectorSource<Geometry>>;
      const visible =
        !geofenceFilter?.layerId || geofenceFilter.layerId === layer.getSource().get('id');
      lyr.setVisible(visible);
      if (visible) {
        applyGeofenceFilterToLayerOnMap(layer, {
          ...geofenceFilter,
        });
      }
      layer.setStyle(updateGeofencesStyle(new Map()));
    });
};

export const getLastestFenceEvent = (
  assetState?: AssetState,
): AssetCoordinatesTriggered | AssetCoordinatesDwell | undefined => {
  if (!assetState) return;
  const { lastFenceEventBackend, lastFenceEventMobile } = assetState;

  if (!lastFenceEventBackend) return lastFenceEventMobile;
  if (!lastFenceEventMobile) return lastFenceEventBackend;

  return lastFenceEventBackend.iso8601 > lastFenceEventMobile.iso8601
    ? lastFenceEventBackend
    : lastFenceEventMobile;
};

export const getSmallestAreaFeature = <T extends RenderFeature | Feature<Geometry>>(
  features: T[],
): T | undefined => {
  if (!features.length) return undefined;

  let smallest = Number.MAX_SAFE_INTEGER;
  let result = features[0];

  for (const feature of features) {
    const extent = feature.getGeometry()?.getExtent();
    if (!extent) continue;

    const area = Math.abs(extent[2] - extent[0]) * Math.abs(extent[3] - extent[1]);

    if (area < smallest) {
      smallest = area;
      result = feature;
    }
  }
  return result;
};
